import { fetchAuthSession } from 'aws-amplify/auth';
import { withTimeout, AUTH_TIMEOUT_MS } from './network';
import { getBilledPricing, getIntroTrial } from './iapPricing';

const API_BASE = import.meta.env.VITE_API_BASE_URL || '';

const APPLE_PRODUCT_IDS = {
  monthly: 'uk.co.wolftechnologies.powderlog.premium.monthly',
  yearly: 'uk.co.wolftechnologies.powderlog.premium.yearly',
};

const APPLE_TO_INTERNAL = Object.fromEntries(
  Object.entries(APPLE_PRODUCT_IDS).map(([k, v]) => [v, k]),
);

// All recurring subscription products grant the same 'premium' tier; the
// period (monthly/yearly) is metadata for receipts/UI only.
const PLAN_TO_TIER = {
  monthly: 'premium',
  yearly: 'premium',
};

// Pricing helpers live in ./iapPricing.js (pure, unit-tested). The billed
// amount MUST come from getBilledPricing — never `product.pricing.price`,
// which is the £0.00 trial phase on any intro-offer-eligible account.

// CdvPurchase cancel codes that MUST be treated as silent user cancels.
// Apple's docs only mention 6777006 but real-device sandbox cancels have
// surfaced as 6777003 and 6777010 — see shared-docs/in-app-purchases.md.
const CANCEL_CODES = new Set([6777006, 6777003, 6777010]);

function isCancelError(result) {
  if (!result) return false;
  if (CANCEL_CODES.has(result.code)) return true;
  return /cancel/i.test(result.message || '');
}

const PRODUCTS = {
  monthly: {
    id: 'powderlog_monthly',
    appleProductId: APPLE_PRODUCT_IDS.monthly,
    name: 'Premium Monthly',
    price: 3.99,
    currency: 'GBP',
    type: 'paid subscription',
  },
  yearly: {
    id: 'powderlog_yearly',
    appleProductId: APPLE_PRODUCT_IDS.yearly,
    name: 'Premium Yearly',
    price: 19.99,
    currency: 'GBP',
    type: 'paid subscription',
  },
};

function isNativeApp() {
  return typeof window !== 'undefined' && window.Capacitor?.isNativePlatform?.();
}
function isIOS() {
  return isNativeApp() && window.Capacitor?.getPlatform?.() === 'ios';
}

async function getIdToken() {
  try {
    const session = await withTimeout(fetchAuthSession(), AUTH_TIMEOUT_MS, 'fetchAuthSession');
    return session?.tokens?.idToken?.toString() ?? null;
  } catch {
    return null;
  }
}

let iapStore = null;
let iapInitialized = false;
let userInitiatedAt = 0;
const USER_INITIATED_WINDOW_MS = 5 * 60 * 1000;

// Transactions the user just approved (purchase or restore), keyed
// transactionId → productId. `approved` is the only place the id is reliably
// known; `verified` drains the queue. The object CdvPurchase hands to
// `verified` is a VerifiedReceipt, which has NO `.transaction` and NO
// `.productId` — the old handler read them, got undefined, and the
// apple-verify POST never fired, so no Apple purchase was ever recorded
// server-side (fleet fix 2026-09-05, canon: shared-docs/in-app-purchases.md).
const pendingVerify = new Map();

async function silentVerify(transactionId, isRestore = false) {
  if (!transactionId) return;
  const idToken = await getIdToken();
  if (!idToken) return;
  const path = isRestore ? '/subscription/restore' : '/subscription/apple-verify';
  try {
    const res = await fetch(`${API_BASE}${path}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: idToken },
      body: JSON.stringify({ transactionId }),
    });
    if (!res.ok) console.warn('IAP verify failed:', res.status);
  } catch (err) {
    console.warn('IAP verify error:', err);
  }
}

async function initializeIAP() {
  if (!isNativeApp() || iapInitialized) return;

  const { store, Platform, ProductType } = window.CdvPurchase || {};
  if (!store) {
    console.warn('IAP store not available');
    return;
  }

  iapStore = store;
  const platform = isIOS() ? Platform.APPLE_APPSTORE : Platform.GOOGLE_PLAY;
  iapStore.register([
    { id: APPLE_PRODUCT_IDS.monthly, type: ProductType.PAID_SUBSCRIPTION, platform },
    { id: APPLE_PRODUCT_IDS.yearly, type: ProductType.PAID_SUBSCRIPTION, platform },
  ]);

  // CdvPurchase v13: each handler must be registered on its OWN store.when()
  // call — chaining .approved(fn).verified(fn) throws at init.
  iapStore.when().approved((transaction) => {
    if (!transaction.transactionId) {
      transaction.finish();
      return;
    }
    const appleId = transaction.products?.[0]?.id;
    const planId = APPLE_TO_INTERNAL[appleId];
    const tier = planId ? PLAN_TO_TIER[planId] : null;
    // Cross-user receipt contamination guard: only verify inside the
    // user-initiated (purchase/restore) window.
    if (Date.now() - userInitiatedAt >= USER_INITIATED_WINDOW_MS) {
      transaction.finish();
      return;
    }
    // CdvPurchase's Transaction exposes `expirationDate` (a Date), never
    // `expiryDate`. Apple rarely populates it client-side, so this skip is
    // best-effort only — the backend apple-verify enforces expiry.
    const expiresAt = transaction.expirationDate ? new Date(transaction.expirationDate) : null;
    const isExpired = Boolean(expiresAt && expiresAt.getTime() <= Date.now());
    // Unknown product = the plugin's virtual `appstore.application`
    // transaction (always first in the Apple receipt, fires approved like a
    // sale) or an unregistered SKU — never queue it for the backend.
    if (!tier || isExpired) {
      transaction.finish();
      return;
    }

    // OPTIMISTIC ONLY — `approved` is not proof of entitlement. The Apple
    // adapter never populates expirationDate (SKTransaction.refresh sets only
    // products/originalTransactionId/purchaseDate), so `isExpired` above is
    // always false on iOS and a Restore of a lapsed subscription lands here.
    // The post-verify reconcile below re-reads GET /subscription/status and
    // drops this grant when the backend refused to link the transaction.
    window.dispatchEvent(
      new CustomEvent('purchase:complete', {
        detail: {
          planId,
          tier,
          source: 'iap_apple',
          expiresAt: expiresAt ? expiresAt.toISOString() : null,
        },
      }),
    );

    // Queue the id for `verified`, then advance the CdvPurchase state
    // machine so `verified` fires and we can notify our backend + finish.
    pendingVerify.set(transaction.transactionId, appleId);
    transaction.verify();
  });

  iapStore.when().verified((receipt) => {
    // `receipt` is a VerifiedReceipt — the local transactions live on
    // receipt.sourceReceipt.transactions. Send exactly what `approved`
    // queued (already window-gated), and only ids this receipt carries.
    const localIds = new Set(
      (receipt?.sourceReceipt?.transactions || []).map((t) => t.transactionId).filter(Boolean),
    );
    const writes = [];
    for (const [transactionId] of pendingVerify) {
      if (localIds.size && !localIds.has(transactionId)) continue;
      pendingVerify.delete(transactionId);
      // silentVerify never rejects and never toasts (stale receipts re-fire
      // on every launch).
      writes.push(silentVerify(transactionId));
    }
    receipt.finish();
    if (writes.length) {
      // Refetch server truth only AFTER the writes settle — firing it early
      // reads a row that does not exist yet and snaps the optimistic Premium
      // back to Free. This reconcile is also the ONLY thing that can revoke
      // the optimistic grant `approved` dispatched: apple-verify answers a
      // lapsed transaction with 200 {success:false,status:'expired'} (and a
      // cross-account receipt with 409, an Apple lookup failure with 502),
      // none of which write a row — see hooks/useSubscriptionSync.js.
      Promise.allSettled(writes).then(() => {
        window.dispatchEvent(new CustomEvent('subscription:refresh'));
      });
    }
  });

  await iapStore.initialize();
  iapInitialized = true;
}

async function purchaseWithIAP(planId) {
  if (!iapStore) throw new Error('IAP not initialized');
  const appleId = APPLE_PRODUCT_IDS[planId];
  if (!appleId) throw new Error(`No Apple IAP for plan: ${planId}`);
  const offer = iapStore.get(appleId)?.getOffer();
  if (!offer) throw new Error(`Product ${appleId} not found in store`);

  userInitiatedAt = Date.now();
  const result = await iapStore.order(offer);
  if (result && result.isError) {
    if (isCancelError(result)) {
      const cancelled = new Error('canceled');
      cancelled.cancelled = true;
      throw cancelled;
    }
    throw new Error(result.message || `Purchase failed (code ${result.code || 'unknown'})`);
  }
  return result;
}

export async function initialize() {
  if (isNativeApp()) await initializeIAP();
}

export async function purchase(planId) {
  if (!PRODUCTS[planId]) throw new Error(`Unknown plan: ${planId}`);
  if (isNativeApp()) return purchaseWithIAP(planId);
  // Apple IAP is the only payment path — web has no checkout.
  throw new Error('Subscriptions are available in the PowderLog iOS app.');
}

export function getProducts() {
  if (isNativeApp() && iapStore) {
    return Object.entries(PRODUCTS)
      .filter(([, product]) => !product.webOnly)
      .map(([key, product]) => {
        const iapProduct = iapStore.get(product.appleProductId);
        // Recurring billed amount (Guideline 3.1.2(c)) — the paywall renders
        // this as its largest pricing element and on the CTA, so it must be
        // the price the user is charged each period, never the intro phase.
        const billed = getBilledPricing(iapProduct);
        return {
          ...product,
          planId: key,
          localPrice: billed?.price || `£${product.price}`,
          currency: billed?.currency || 'GBP',
          introTrial: getIntroTrial(iapProduct),
        };
      });
  }
  return Object.entries(PRODUCTS).map(([key, product]) => ({
    ...product,
    planId: key,
    localPrice: `£${product.price}`,
    currency: 'GBP',
    introTrial: null,
  }));
}

export async function restorePurchases() {
  if (!isNativeApp() || !iapStore) return [];
  userInitiatedAt = Date.now();
  // Same contract as store.order(): a StoreKit restore failure RESOLVES with
  // an IError rather than throwing, so surface it for the caller's feedback.
  const result = await iapStore.restorePurchases();
  // A cancel, or 6777011 REFRESH_RECEIPTS (the adapter refreshes the receipt
  // AFTER the restored transactions have been delivered, so it does not mean
  // the restore failed), must never surface as an error — a red toast on a
  // purchase control is the Guideline 2.1(b) pattern.
  if (result && result.isError && !isCancelError(result) && result.code !== 6777011) {
    const err = new Error(result.message || 'Restore failed');
    err.code = result.code;
    throw err;
  }
  return result;
}

export function getSubscriptionStatus() {
  if (isNativeApp() && iapStore) {
    const yearlySub = iapStore.get(APPLE_PRODUCT_IDS.yearly);
    const monthlySub = iapStore.get(APPLE_PRODUCT_IDS.monthly);
    const isOwned = (p) => p?.owned;
    if (isOwned(yearlySub)) return { tier: 'premium', source: 'iap', active: true };
    if (isOwned(monthlySub)) return { tier: 'premium', source: 'iap', active: true };
    return { tier: 'free', source: 'none', active: false };
  }
  return null;
}

export { PRODUCTS, APPLE_PRODUCT_IDS, APPLE_TO_INTERNAL, isNativeApp };
